"use client";

import { Link } from "@/i18n/navigation";
import {
  PhoneIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";
import CtaLink from "@/components/ui/CtaLink";
import Contact from "@/components/Contact/Contact";

const PHONE_TEL = "+32493966507";
const PHONE_DISPLAY = "+32 (0)493 96 65 07";

const CONTENT = {
  fr: {
    h1: "Vide maison à Schaerbeek : le guide pratique",
    heroSubtitle:
      "Situations qui amènent à faire appel à un vide maison, déroulement concret de l'intervention, facteurs de prix et sort des objets de valeur : ce guide répond aux questions que se posent la plupart des Schaerbeekois avant de nous contacter.",
    ctaCall: "Appeler maintenant",
    ctaQuote: "Demander un devis gratuit",
    introBefore: "Organiser un vide maison à Schaerbeek soulève souvent les mêmes questions : dans quelles situations faut-il faire appel à un professionnel, comment se déroule concrètement l'intervention, qu'est-ce qui influence le prix, et que deviennent les objets qui ont une valeur réelle. Ce guide reprend ces points dans l'ordre où ils se posent habituellement, du premier contact jusqu'au bien vidé et prêt à être rendu ou vendu. Vous y trouverez aussi les erreurs les plus fréquentes à éviter et les réponses aux questions qu'on nous pose le plus souvent. Pour les particularités propres aux différents quartiers de la commune, notre page ",
    introLink: "vide maison à Schaerbeek",
    introAfter: " complète utilement ce guide.",
    s1: {
      title: "Dans quelles situations faire appel à un vide maison ?",
      intro: "La demande arrive rarement sans raison précise. Voici les situations les plus courantes que nous rencontrons à Schaerbeek :",
      items: [
        "Succession et maison familiale à vider avant partage ou vente",
        "Déménagement vers un logement plus petit ou une maison de repos",
        "Fin de bail avec un délai de remise des clés à respecter",
        "Bien à débarrasser avant une mise en vente ou une rénovation",
        "Cave, grenier ou garage encombrés qu'il faut enfin désencombrer",
      ],
      outroBefore: "Selon le type de bien concerné, retrouvez le détail de nos prestations pour un ",
      outroLink1: "débarras de maison",
      outroMid: ", un ",
      outroLink2: "débarras d'appartement",
      outroMid2: " ou une intervention limitée à une ",
      outroLink3: "cave, un grenier ou un garage",
      outroAfter: ".",
    },
    s2: {
      title: "Comment se déroule une intervention, étape par étape",
      p: "Le premier contact sert à cerner la situation : type de bien, volume approximatif, accès et calendrier souhaité. Une estimation est ensuite donnée par téléphone ou sur base de photos ; une visite sur place complète parfois cette étape pour les biens plus volumineux ou complexes. Le devis validé, le jour de l'intervention commence toujours par un temps de tri : ce qui est conservé par la famille, ce qui peut être racheté, et ce qui part en évacuation. Suivent le chargement du camion, l'évacuation proprement dite, puis un nettoyage sommaire du bien si celui-ci a été demandé.",
    },
    s3: {
      title: "Ce qui fait varier le prix d'un vide maison",
      pBefore: "Il n'existe pas de tarif fixe : deux biens de surface comparable peuvent demander un temps de travail très différent selon le volume réellement stocké et l'accès au bien. Une maison de maître avec cave et grenier n'entre pas dans le même calcul qu'un appartement d'un seul niveau facilement accessible. Ces différences de bâti sont d'ailleurs très marquées à Schaerbeek d'un quartier à l'autre — notre page ",
      pLink1: "vide maison à Schaerbeek",
      pMid: " en détaille les particularités locales. Le fonctionnement général des devis est expliqué sur notre page ",
      pLink2: "tarifs",
      pAfter: ".",
    },
    s4: {
      title: "Trier avant d'évacuer : ce qui peut être racheté",
      pBefore: "Avant toute évacuation, les objets trouvés sur place sont examinés : mobilier ancien, argenterie, bijoux, tableaux, livres ou objets de collection. Lorsqu'ils ont une réelle valeur marchande, ils sont rachetés et ce montant vient en déduction du prix du débarras. C'est une étape fréquente dans les successions de maisons occupées depuis longtemps par une même famille, où les objets se sont accumulés au fil des générations. Pour apprendre à repérer vous-même ce qui pourrait valoir la peine d'être estimé, notre article ",
      pLink1: "reconnaître un objet de valeur avant un rachat",
      pMid: " donne quelques repères concrets. Le fonctionnement complet est détaillé sur notre page ",
      pLink2: "rachat d'objets",
      pAfter: ".",
    },
    ctaBox: {
      title: "Une situation précise à Schaerbeek ?",
      text: "Décrivez-nous le bien et son accès : nous revenons vers vous avec une première estimation gratuite.",
      cta: "Demander un devis",
    },
    s5: {
      title: "Préparer l'accès et la logistique",
      pBefore: "L'accès pèse souvent autant que le volume dans l'organisation d'une intervention : étage à desservir, présence ou non d'un ascenseur, largeur des couloirs et des cages d'escalier, possibilité de garer le camion à proximité immédiate. Dans un immeuble en copropriété, prévenir le syndic à l'avance permet de réserver l'ascenseur ou de signaler l'occupation temporaire des parties communes. Signaler ces éléments dès la demande de devis, plutôt que le jour même, évite les temps morts et permet de mieux estimer la durée réelle du chantier. Notre article ",
      pLink: "checklist avant un vide maison",
      pAfter: " reprend point par point ce qu'il est utile de préparer.",
    },
    s6: {
      title: "Erreurs fréquentes à éviter",
      mistakes: [
        {
          title: "Ne signaler que les pièces de vie",
          text: "Oublier de mentionner une cave, un grenier ou un garage encombré fausse l'estimation initiale : ces espaces représentent parfois un volume aussi important que le reste du logement.",
        },
        {
          title: "Sous-estimer un accès difficile",
          text: "Un étage sans ascenseur, un couloir étroit ou une rue où le camion ne peut pas se garer juste devant allonge le temps de travail. Le signaler tôt évite les mauvaises surprises le jour J.",
        },
        {
          title: "Évacuer avant d'avoir trié les objets de famille",
          text: "Dans une succession en particulier, se débarrasser trop vite d'un meuble ou d'un carton sans l'avoir examiné fait parfois perdre un objet à valeur marchande ou affective.",
        },
        {
          title: "Attendre le dernier moment pour un délai serré",
          text: "Une date de remise de clés ou de compromis de vente déjà fixée laisse moins de marge pour organiser le tri. Prévenir dès que la date se précise permet de mieux s'organiser.",
        },
      ],
    },
    s7: {
      title: "Un cas particulier : la succession",
      pBefore: "Une bonne part des demandes à Schaerbeek concerne des maisons occupées par la même famille sur plusieurs décennies. Ce contexte demande souvent davantage de temps en amont : les héritiers doivent s'accorder sur ce qui est conservé, partagé ou évacué, avant même que le devis ne soit fixé. Nous nous adaptons à ce rythme plutôt que de le précipiter. Notre article ",
      pLink: "vide maison après une succession",
      pAfter: " détaille les démarches propres à cette situation.",
    },
    retenirTitle: "À retenir",
    retenir: [
      "Succession, déménagement, fin de bail ou mise en vente : chaque situation suit le même déroulement, adapté à votre calendrier",
      "L'estimation se fait par téléphone, par photos ou lors d'une visite sur place selon la taille du bien",
      "Le prix dépend du volume réel et de l'accès, pas d'un tarif fixe au m³",
      "Les objets de valeur sont examinés avant évacuation et rachetés lorsqu'ils en ont une",
      "Signaler l'accès et les espaces annexes dès la demande évite les mauvaises surprises",
      "L'estimation est gratuite et sans engagement",
    ],
    faqTitle: "Questions fréquentes",
    faq: [
      {
        q: "Combien de temps s'écoule entre la demande et l'intervention à Schaerbeek ?",
        a: "Cela dépend surtout de votre disponibilité et de la nôtre au moment de la demande. Pour une situation urgente, fin de bail imminente ou remise de clés déjà programmée, nous cherchons le créneau le plus rapproché possible. Pour une succession sans contrainte de date, rien n'empêche de prendre le temps nécessaire au tri avant de fixer un jour d'intervention. Le mieux reste de nous contacter dès que la date probable se dessine, même approximative.",
      },
      {
        q: "Comment obtenir un devis sans devoir attendre une visite sur place ?",
        a: "Pour beaucoup de biens, une description précise par téléphone ou quelques photos par pièce suffisent à établir une première estimation : volume approximatif, nombre d'étages, présence d'un ascenseur, espaces annexes comme une cave ou un grenier. Une visite reste utile pour les biens plus volumineux ou complexes, afin de confirmer le devis avant l'intervention. Dans tous les cas, l'estimation est gratuite et sans engagement.",
      },
      {
        q: "Le débarras est-il possible dans un immeuble géré par un syndic ?",
        a: "Oui. Dans les immeubles à appartements, il est utile de prévenir le syndic à l'avance, notamment pour réserver l'ascenseur ou signaler l'occupation temporaire des parties communes le jour de l'intervention. Nous nous adaptons aux règles propres à chaque copropriété et pouvons échanger directement avec le syndic si cela facilite l'organisation, en particulier lorsque le bien n'est pas occupé par la personne qui nous contacte.",
      },
      {
        q: "Que faire des papiers, photos et documents personnels trouvés sur place ?",
        a: "Ces éléments ne sont jamais évacués sans distinction : nous les mettons de côté dès qu'ils sont repérés, pour que la famille puisse les examiner avant toute décision. Dans une succession notamment, des documents utiles — actes, correspondances, photos anciennes — se retrouvent parfois mélangés à des papiers sans valeur. Le tri initial permet justement d'éviter qu'ils partent avec le reste par manque d'attention.",
      },
      {
        q: "Les déchets sont-ils triés ou tout part-il ensemble ?",
        a: "Le tri fait partie du travail : les matériaux valorisables — métal, bois, textiles, appareils électroniques — sont orientés vers des filières adaptées plutôt que mélangés à du tout-venant lorsque c'est possible. Cela concerne aussi bien le contenu d'une cave ou d'un grenier que le mobilier des pièces de vie. L'objectif est d'évacuer proprement, pas seulement de vider vite.",
      },
      {
        q: "Un vide maison partiel, une seule pièce ou une cave, est-il possible ?",
        a: "Oui, l'intervention n'implique pas nécessairement tout le bien. Il est tout à fait possible de limiter la demande à une pièce, une cave ou un grenier, par exemple pour désencombrer un espace précis sans toucher au reste du logement. Le devis est alors établi uniquement sur ce périmètre réduit.",
      },
      {
        q: "Peut-on prévoir un nettoyage sommaire après l'évacuation, avant une remise des clés ?",
        a: "Oui, un nettoyage sommaire du bien vidé peut être prévu en complément de l'évacuation, ce qui est souvent utile avant une remise des clés à un propriétaire, un syndic ou un acheteur. Il ne s'agit pas d'un nettoyage professionnel en profondeur, mais d'un bien laissé débarrassé et balayé, prêt à être visité ou rendu sans meubles ni déchets visibles.",
      },
    ],
    finalCta: {
      title: "Prêt à organiser votre vide maison à Schaerbeek ?",
      text: "Décrivez-nous votre situation : nous vous répondons avec une première estimation gratuite et sans engagement.",
      quote: "Demander un devis gratuit",
    },
  },
  nl: {
    h1: "Huis leegmaken in Schaarbeek: de praktische gids",
    heroSubtitle:
      "Situaties die aanleiding geven tot een huis leegmaken, het concrete verloop van de interventie, prijsfactoren en het lot van waardevolle voorwerpen: deze gids beantwoordt de vragen die de meeste Schaarbekenaars zich stellen vóór ze ons contacteren.",
    ctaCall: "Bel nu",
    ctaQuote: "Vraag een gratis offerte aan",
    introBefore: "Een huis leegmaken organiseren in Schaarbeek roept vaak dezelfde vragen op: in welke situaties moet u een professional inschakelen, hoe verloopt de interventie concreet, wat beïnvloedt de prijs, en wat gebeurt er met voorwerpen die een werkelijke waarde hebben. Deze gids behandelt deze punten in de volgorde waarin ze zich gewoonlijk voordoen, van het eerste contact tot het leeggemaakte pand dat klaar is om teruggegeven of verkocht te worden. U vindt er ook de meest voorkomende fouten om te vermijden en de antwoorden op de vragen die ons het vaakst gesteld worden. Voor de eigenheden van de verschillende wijken van de gemeente vormt onze pagina ",
    introLink: "huis leegmaken in Schaarbeek",
    introAfter: " een nuttige aanvulling op deze gids.",
    s1: {
      title: "In welke situaties een huis leegmaken inschakelen?",
      intro: "De aanvraag komt zelden zonder specifieke reden. Hier zijn de meest voorkomende situaties die wij in Schaarbeek tegenkomen:",
      items: [
        "Nalatenschap en familiehuis te ledigen vóór verdeling of verkoop",
        "Verhuizing naar een kleinere woning of een rusthuis",
        "Einde van een huurcontract met een sleuteloverdracht binnen een bepaalde termijn",
        "Pand te ontruimen vóór een verkoop of een renovatie",
        "Volgestouwde kelder, zolder of garage die eindelijk opgeruimd moet worden",
      ],
      outroBefore: "Afhankelijk van het type pand vindt u de details van onze diensten voor een ",
      outroLink1: "huisontruiming",
      outroMid: ", een ",
      outroLink2: "appartementsontruiming",
      outroMid2: " of een interventie beperkt tot een ",
      outroLink3: "kelder, zolder of garage",
      outroAfter: ".",
    },
    s2: {
      title: "Hoe verloopt een interventie, stap voor stap",
      p: "Het eerste contact dient om de situatie te verduidelijken: type pand, geschat volume, toegang en gewenste planning. Vervolgens wordt telefonisch of op basis van foto's een schatting gegeven; een bezoek ter plaatse vult deze stap soms aan voor omvangrijkere of complexere panden. Zodra de offerte bevestigd is, begint de dag van de interventie steeds met een sorteermoment: wat door de familie bewaard wordt, wat ingekocht kan worden, en wat wordt afgevoerd. Daarna volgen het laden van de vrachtwagen, de eigenlijke afvoer, en vervolgens een basisreiniging van het pand indien deze gevraagd werd.",
    },
    s3: {
      title: "Wat de prijs van een huis leegmaken doet variëren",
      pBefore: "Er bestaat geen vast tarief: twee panden van vergelijkbare oppervlakte kunnen een zeer verschillende werktijd vergen naargelang het werkelijk opgeslagen volume en de toegang tot het pand. Een herenhuis met kelder en zolder valt niet onder dezelfde berekening als een makkelijk toegankelijk appartement op één verdieping. Deze verschillen in bebouwing zijn trouwens erg uitgesproken in Schaarbeek, van wijk tot wijk — onze pagina ",
      pLink1: "huis leegmaken in Schaarbeek",
      pMid: " beschrijft de lokale eigenheden in detail. De algemene werking van de offertes wordt uitgelegd op onze pagina ",
      pLink2: "tarieven",
      pAfter: ".",
    },
    s4: {
      title: "Sorteren vóór de afvoer: wat kan worden ingekocht",
      pBefore: "Vóór elke afvoer worden de ter plaatse gevonden voorwerpen onderzocht: antiek meubilair, zilverwerk, juwelen, schilderijen, boeken of verzamelvoorwerpen. Wanneer ze een echte marktwaarde hebben, worden ze ingekocht en wordt dit bedrag van de ontruimingsprijs afgetrokken. Dit is een veelvoorkomende stap bij nalatenschappen van huizen die al lang door dezelfde familie bewoond worden, waar voorwerpen zich doorheen de generaties opgestapeld hebben. Om zelf te leren herkennen wat het waard zou kunnen zijn om geschat te worden, geeft ons artikel ",
      pLink1: "een waardevol voorwerp herkennen vóór een inkoop",
      pMid: " enkele concrete richtlijnen. De volledige werking wordt beschreven op onze pagina ",
      pLink2: "inkoop van voorwerpen",
      pAfter: ".",
    },
    ctaBox: {
      title: "Een specifieke situatie in Schaarbeek?",
      text: "Beschrijf ons het pand en de toegang: wij komen bij u terug met een eerste gratis schatting.",
      cta: "Vraag een offerte aan",
    },
    s5: {
      title: "De toegang en de logistiek voorbereiden",
      pBefore: "De toegang weegt vaak even zwaar door als het volume bij de organisatie van een interventie: te bedienen verdieping, al dan niet aanwezigheid van een lift, breedte van de gangen en trappenhuizen, mogelijkheid om de vrachtwagen vlak in de buurt te parkeren. In een gebouw met mede-eigendom laat het vooraf verwittigen van de syndicus toe de lift te reserveren of de tijdelijke bezetting van de gemeenschappelijke delen te signaleren. Deze elementen vanaf de offerteaanvraag signaleren, in plaats van op de dag zelf, voorkomt stilstand en laat toe de werkelijke duur van de werf beter in te schatten. Ons artikel ",
      pLink: "checklist vóór een huis leegmaken",
      pAfter: " herneemt punt per punt wat nuttig is om voor te bereiden.",
    },
    s6: {
      title: "Veelvoorkomende fouten om te vermijden",
      mistakes: [
        {
          title: "Enkel de leefruimtes signaleren",
          text: "Vergeten een kelder, zolder of volgestouwde garage te vermelden vertekent de eerste schatting: deze ruimtes vertegenwoordigen soms een even groot volume als de rest van de woning.",
        },
        {
          title: "Een moeilijke toegang onderschatten",
          text: "Een verdieping zonder lift, een smalle gang of een straat waar de vrachtwagen niet vlak voor kan parkeren, verlengt de werktijd. Dit vroeg signaleren voorkomt onaangename verrassingen op de grote dag.",
        },
        {
          title: "Afvoeren vóór het sorteren van familiestukken",
          text: "Vooral bij een nalatenschap kan het te snel wegdoen van een meubelstuk of een doos zonder deze te hebben bekeken soms leiden tot het verlies van een voorwerp met marktwaarde of affectieve waarde.",
        },
        {
          title: "Wachten tot het laatste moment bij een krappe termijn",
          text: "Een reeds vastgelegde datum voor sleuteloverdracht of verkoopcompromis laat minder ruimte om de sortering te organiseren. Verwittigen zodra de datum zich aftekent, laat toe zich beter te organiseren.",
        },
      ],
    },
    s7: {
      title: "Een bijzonder geval: de nalatenschap",
      pBefore: "Een groot deel van de aanvragen in Schaarbeek betreft huizen die door dezelfde familie gedurende meerdere decennia bewoond werden. Deze context vergt vaak meer tijd vooraf: de erfgenamen moeten het eens worden over wat bewaard, verdeeld of afgevoerd wordt, nog vóór de offerte wordt vastgelegd. Wij passen ons aan dit ritme aan in plaats van het te bespoedigen. Ons artikel ",
      pLink: "huis leegmaken na een nalatenschap",
      pAfter: " beschrijft de stappen die eigen zijn aan deze situatie.",
    },
    retenirTitle: "Wat u moet onthouden",
    retenir: [
      "Nalatenschap, verhuizing, einde huurcontract of verkoop: elke situatie volgt hetzelfde verloop, aangepast aan uw planning",
      "De schatting gebeurt telefonisch, via foto's of tijdens een bezoek ter plaatse naargelang de grootte van het pand",
      "De prijs hangt af van het werkelijke volume en de toegang, niet van een vast tarief per m³",
      "Waardevolle voorwerpen worden vóór de afvoer onderzocht en ingekocht wanneer ze waarde hebben",
      "De toegang en de bijruimtes vanaf de aanvraag signaleren voorkomt onaangename verrassingen",
      "De schatting is gratis en vrijblijvend",
    ],
    faqTitle: "Veelgestelde vragen",
    faq: [
      {
        q: "Hoeveel tijd verstrijkt er tussen de aanvraag en de interventie in Schaarbeek?",
        a: "Dit hangt vooral af van uw beschikbaarheid en de onze op het moment van de aanvraag. Voor een dringende situatie, een nakend einde van het huurcontract of een reeds geplande sleuteloverdracht, zoeken we het dichtstbijzijnde mogelijke tijdslot. Voor een nalatenschap zonder tijdsdruk staat niets in de weg om de nodige tijd te nemen voor de sortering vóór een interventiedag vast te leggen. Het beste blijft ons te contacteren zodra de waarschijnlijke datum zich aftekent, ook al is deze nog benaderend.",
      },
      {
        q: "Hoe krijg ik een offerte zonder op een bezoek ter plaatse te moeten wachten?",
        a: "Voor veel panden volstaat een nauwkeurige telefonische beschrijving of enkele foto's per kamer om een eerste schatting op te stellen: geschat volume, aantal verdiepingen, aanwezigheid van een lift, bijruimtes zoals een kelder of zolder. Een bezoek blijft nuttig voor omvangrijkere of complexere panden, om de offerte vóór de interventie te bevestigen. In alle gevallen is de schatting gratis en vrijblijvend.",
      },
      {
        q: "Is de ontruiming mogelijk in een gebouw beheerd door een syndicus?",
        a: "Ja. In appartementsgebouwen is het nuttig de syndicus vooraf te verwittigen, met name om de lift te reserveren of de tijdelijke bezetting van de gemeenschappelijke delen op de dag van de interventie te signaleren. Wij passen ons aan de regels van elke mede-eigendom aan en kunnen rechtstreeks met de syndicus overleggen als dit de organisatie vergemakkelijkt, vooral wanneer het pand niet bewoond wordt door de persoon die ons contacteert.",
      },
      {
        q: "Wat te doen met papieren, foto's en persoonlijke documenten die ter plaatse gevonden worden?",
        a: "Deze elementen worden nooit zonder onderscheid afgevoerd: we zetten ze apart zodra we ze opmerken, zodat de familie ze kan bekijken vóór elke beslissing. Vooral bij een nalatenschap raken nuttige documenten — akten, correspondentie, oude foto's — soms vermengd met waardeloze papieren. De eerste sortering voorkomt juist dat ze door onoplettendheid met de rest meegaan.",
      },
      {
        q: "Wordt het afval gesorteerd of gaat alles samen weg?",
        a: "Sorteren maakt deel uit van het werk: valoriseerbare materialen — metaal, hout, textiel, elektronische toestellen — worden waar mogelijk naar gepaste kanalen geleid in plaats van vermengd met gewoon afval. Dit geldt zowel voor de inhoud van een kelder of zolder als voor het meubilair van de leefruimtes. Het doel is netjes af te voeren, niet enkel snel te ledigen.",
      },
      {
        q: "Is een gedeeltelijk huis leegmaken, één kamer of een kelder, mogelijk?",
        a: "Ja, de interventie hoeft niet noodzakelijk het hele pand te betreffen. Het is heel goed mogelijk om de aanvraag te beperken tot een kamer, een kelder of een zolder, bijvoorbeeld om een specifieke ruimte op te ruimen zonder de rest van de woning aan te raken. De offerte wordt dan enkel op basis van deze beperkte omvang opgesteld.",
      },
      {
        q: "Kan een basisreiniging na de afvoer voorzien worden, vóór een sleuteloverdracht?",
        a: "Ja, een basisreiniging van het leeggemaakte pand kan als aanvulling op de afvoer voorzien worden, wat vaak nuttig is vóór een sleuteloverdracht aan een eigenaar, een syndicus of een koper. Het gaat niet om een grondige professionele reiniging, maar om een pand dat opgeruimd en geveegd wordt achtergelaten, klaar om bezocht of teruggegeven te worden zonder zichtbare meubels of afval.",
      },
    ],
    finalCta: {
      title: "Klaar om uw huis leegmaken in Schaarbeek te organiseren?",
      text: "Beschrijf ons uw situatie: wij antwoorden u met een eerste gratis en vrijblijvende schatting.",
      quote: "Vraag een gratis offerte aan",
    },
  },
  en: {
    h1: "House clearance in Schaerbeek: the practical guide",
    heroSubtitle:
      "Situations that lead to a house clearance, how the intervention actually unfolds, pricing factors and what happens to valuable items: this guide answers the questions most Schaerbeek residents ask before contacting us.",
    ctaCall: "Call now",
    ctaQuote: "Request a free quote",
    introBefore: "Organising a house clearance in Schaerbeek often raises the same questions: in which situations should you call on a professional, how does the intervention actually unfold, what affects the price, and what happens to items with real value. This guide covers these points in the order they usually come up, from the first contact through to the emptied property being ready to hand back or sell. You'll also find the most common mistakes to avoid and answers to the questions we're asked most often. For the particularities of the commune's different neighbourhoods, our ",
    introLink: "house clearance in Schaerbeek",
    introAfter: " page usefully complements this guide.",
    s1: {
      title: "When should you call on a house clearance service?",
      intro: "The request rarely comes without a specific reason. Here are the most common situations we encounter in Schaerbeek:",
      items: [
        "Estate settlement and family home to clear before division or sale",
        "Moving to a smaller home or a care home",
        "End of a lease with a key handover deadline to meet",
        "Property to clear before putting it up for sale or renovating",
        "Cluttered cellar, attic or garage that finally needs sorting out",
      ],
      outroBefore: "Depending on the type of property involved, find the details of our services for a ",
      outroLink1: "house clearance",
      outroMid: ", an ",
      outroLink2: "apartment clearance",
      outroMid2: " or an intervention limited to a ",
      outroLink3: "cellar, attic or garage",
      outroAfter: ".",
    },
    s2: {
      title: "How an intervention unfolds, step by step",
      p: "The first contact is used to pin down the situation: type of property, approximate volume, access and desired timeframe. An estimate is then given by phone or based on photos; an on-site visit sometimes completes this step for larger or more complex properties. Once the quote is confirmed, the day of the intervention always starts with a sorting phase: what the family keeps, what can be bought back, and what goes for removal. Loading the truck follows, then the actual removal, then a basic clean of the property if this was requested.",
    },
    s3: {
      title: "What makes the price of a house clearance vary",
      pBefore: "There's no fixed rate: two properties of comparable size can require very different amounts of work depending on the volume actually stored and the access to the property. A townhouse with a cellar and attic doesn't factor into the same calculation as an easily accessible single-storey apartment. These differences in building stock are, in fact, very pronounced across Schaerbeek's neighbourhoods — our ",
      pLink1: "house clearance in Schaerbeek",
      pMid: " page details the local particularities. The general workings of quotes are explained on our ",
      pLink2: "prices",
      pAfter: " page.",
    },
    s4: {
      title: "Sorting before removal: what can be bought back",
      pBefore: "Before any removal, the items found on site are examined: antique furniture, silverware, jewellery, paintings, books or collectable items. When they have real market value, they're bought back and this amount is deducted from the clearance price. This is a common step in estate settlements of homes occupied for a long time by the same family, where items have built up over generations. To learn how to spot yourself what might be worth having appraised, our article ",
      pLink1: "recognising a valuable item before a buyback",
      pMid: " gives a few concrete pointers. The full process is detailed on our ",
      pLink2: "item buyback",
      pAfter: " page.",
    },
    ctaBox: {
      title: "A specific situation in Schaerbeek?",
      text: "Describe the property and its access to us: we'll get back to you with an initial free estimate.",
      cta: "Request a quote",
    },
    s5: {
      title: "Preparing access and logistics",
      pBefore: "Access often weighs as heavily as volume in organising an intervention: floor to be served, presence or absence of a lift, width of corridors and stairwells, ability to park the truck right nearby. In a co-owned building, notifying the managing agent in advance allows the lift to be reserved or the temporary use of communal areas to be flagged. Flagging these elements as soon as you request a quote, rather than on the day itself, avoids downtime and allows the actual duration of the job to be better estimated. Our ",
      pLink: "checklist before a house clearance",
      pAfter: " covers, point by point, what's useful to prepare.",
    },
    s6: {
      title: "Common mistakes to avoid",
      mistakes: [
        {
          title: "Only mentioning the living areas",
          text: "Forgetting to mention a cellar, attic or cluttered garage skews the initial estimate: these spaces sometimes represent as large a volume as the rest of the home.",
        },
        {
          title: "Underestimating difficult access",
          text: "A floor with no lift, a narrow corridor, or a street where the truck can't park right outside, extends the working time. Flagging it early avoids unpleasant surprises on the day.",
        },
        {
          title: "Removing items before sorting family belongings",
          text: "In an estate settlement especially, getting rid of a piece of furniture or a box too quickly without examining it can sometimes mean losing an item of market or sentimental value.",
        },
        {
          title: "Waiting until the last moment for a tight deadline",
          text: "An already-fixed date for a key handover or sale agreement leaves less room to organise the sorting. Notifying us as soon as the date becomes clear allows for better organisation.",
        },
      ],
    },
    s7: {
      title: "A special case: estate settlements",
      pBefore: "A good share of the requests in Schaerbeek concern homes occupied by the same family for several decades. This context often requires more time upfront: heirs need to agree on what's kept, shared or removed, even before the quote is set. We adapt to this pace rather than rushing it. Our article ",
      pLink: "house clearance after an estate settlement",
      pAfter: " details the steps specific to this situation.",
    },
    retenirTitle: "Key takeaways",
    retenir: [
      "Estate settlement, move, end of lease or sale: every situation follows the same process, adapted to your schedule",
      "The estimate is done by phone, from photos, or during an on-site visit depending on the property's size",
      "The price depends on the actual volume and access, not a fixed rate per cubic metre",
      "Valuable items are examined before removal and bought back when they have value",
      "Flagging access and ancillary spaces as soon as you make the request avoids unpleasant surprises",
      "The estimate is free and comes with no obligation",
    ],
    faqTitle: "Frequently asked questions",
    faq: [
      {
        q: "How much time passes between the request and the intervention in Schaerbeek?",
        a: "This mainly depends on your availability and ours at the time of the request. For an urgent situation, an imminent end of lease or an already scheduled key handover, we look for the closest possible slot. For an estate settlement with no date pressure, nothing stops you taking the time needed for sorting before fixing an intervention day. It's best to contact us as soon as a likely date takes shape, even approximately.",
      },
      {
        q: "How do I get a quote without having to wait for an on-site visit?",
        a: "For many properties, a precise description over the phone or a few photos per room are enough to draw up a first estimate: approximate volume, number of floors, presence of a lift, ancillary spaces such as a cellar or attic. A visit remains useful for larger or more complex properties, to confirm the quote before the intervention. In all cases, the estimate is free and comes with no obligation.",
      },
      {
        q: "Is a clearance possible in a building managed by a syndic?",
        a: "Yes. In apartment buildings, it's useful to notify the managing agent in advance, particularly to reserve the lift or flag temporary use of communal areas on the day of the intervention. We adapt to the rules specific to each co-ownership and can liaise directly with the managing agent if that makes organisation easier, particularly when the property isn't occupied by the person contacting us.",
      },
      {
        q: "What should be done with papers, photos and personal documents found on site?",
        a: "These items are never removed indiscriminately: we set them aside as soon as they're spotted, so the family can review them before any decision. In an estate settlement especially, useful documents — deeds, correspondence, old photos — are sometimes found mixed in with worthless papers. This initial sorting precisely avoids them being removed with the rest through oversight.",
      },
      {
        q: "Is waste sorted, or does everything go together?",
        a: "Sorting is part of the job: recoverable materials — metal, wood, textiles, electronic devices — are directed to appropriate channels rather than mixed with general waste wherever possible. This applies both to the contents of a cellar or attic and to the furniture in living areas. The goal is to remove things cleanly, not just quickly.",
      },
      {
        q: "Is a partial house clearance, just one room or a cellar, possible?",
        a: "Yes, the intervention doesn't necessarily have to involve the whole property. It's entirely possible to limit the request to a room, a cellar or an attic, for example to clear a specific space without touching the rest of the home. The quote is then drawn up solely for this reduced scope.",
      },
      {
        q: "Can a basic clean be arranged after removal, before a key handover?",
        a: "Yes, a basic clean of the emptied property can be arranged in addition to the removal, which is often useful before handing the keys back to an owner, managing agent or buyer. This isn't a thorough professional clean, but leaves the property cleared and swept, ready to be viewed or handed over with no visible furniture or waste.",
      },
    ],
    finalCta: {
      title: "Ready to organise your house clearance in Schaerbeek?",
      text: "Describe your situation to us: we'll respond with an initial free, no-obligation estimate.",
      quote: "Request a free quote",
    },
  },
};

export default function PageClient({ locale }) {
  const c = CONTENT[locale];

  return (
    <article>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: c.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        }}
      />

      <section className="relative overflow-hidden bg-sand text-ink">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(900px 480px at 15% -10%, rgba(27,75,51,0.06), transparent 55%)" }} />
        <div className="relative mx-auto max-w-[900px] px-6 py-20 lg:px-10 lg:py-24">
          <h1 className="max-w-2xl font-serif text-4xl font-black leading-[1.1] md:text-5xl">
            {c.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-ink/70 leading-relaxed">
            {c.heroSubtitle}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 rounded-2xl bg-cta-700 px-7 py-3.5 font-semibold text-white shadow-cta transition hover:-translate-y-0.5 hover:bg-cta-800">
              <PhoneIcon className="h-5 w-5" />
              {c.ctaCall}
            </a>
            <a href="#contactus" className="inline-flex items-center gap-2 rounded-2xl border-2 border-ink/15 bg-white px-7 py-3.5 font-semibold text-ink transition hover:border-accent hover:text-accent">
              {c.ctaQuote}
              <ArrowRightIcon className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-[800px] px-6 lg:px-10 space-y-8">
          <div>
            <p className="leading-relaxed text-neutral-700">
              {c.introBefore}
              <Link href="/vide-maison/schaerbeek" className="font-semibold text-accent underline underline-offset-4">
                {c.introLink}
              </Link>
              {c.introAfter}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s1.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s1.intro}</p>
            <ul className="mt-4 space-y-2">
              {c.s1.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-neutral-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.s1.outroBefore}
              <Link href="/debarras-maison" className="font-semibold text-accent underline underline-offset-4">
                {c.s1.outroLink1}
              </Link>
              {c.s1.outroMid}
              <Link href="/debarras-appartement" className="font-semibold text-accent underline underline-offset-4">
                {c.s1.outroLink2}
              </Link>
              {c.s1.outroMid2}
              <Link href="/debarras-cave-grenier-garage" className="font-semibold text-accent underline underline-offset-4">
                {c.s1.outroLink3}
              </Link>
              {c.s1.outroAfter}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s2.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s2.p}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s3.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s3.pBefore}
              <Link href="/vide-maison/schaerbeek" className="font-semibold text-accent underline underline-offset-4">
                {c.s3.pLink1}
              </Link>
              {c.s3.pMid}
              <Link href="/tarifs" className="font-semibold text-accent underline underline-offset-4">
                {c.s3.pLink2}
              </Link>
              {c.s3.pAfter}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s4.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s4.pBefore}
              <Link href="/blog/reconnaitre-objet-valeur-rachat" className="font-semibold text-accent underline underline-offset-4">
                {c.s4.pLink1}
              </Link>
              {c.s4.pMid}
              <Link href="/rachat-objets" className="font-semibold text-accent underline underline-offset-4">
                {c.s4.pLink2}
              </Link>
              {c.s4.pAfter}
            </p>
          </div>

          <div className="rounded-2xl border border-sand-line bg-sand p-6 sm:flex sm:items-center sm:justify-between sm:gap-6">
            <div>
              <p className="font-bold text-ink">{c.ctaBox.title}</p>
              <p className="mt-1 text-sm text-neutral-600">{c.ctaBox.text}</p>
            </div>
            <div className="mt-4 sm:mt-0 sm:shrink-0">
              <CtaLink href="#contactus" variant="accent" icon={ArrowRightIcon}>
                {c.ctaBox.cta}
              </CtaLink>
            </div>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s5.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s5.pBefore}
              <Link href="/blog/checklist-avant-vide-maison" className="font-semibold text-accent underline underline-offset-4">
                {c.s5.pLink}
              </Link>
              {c.s5.pAfter}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s6.title}</h2>
            <div className="mt-4 space-y-3">
              {c.s6.mistakes.map((item) => (
                <div key={item.title} className="flex items-start gap-3 rounded-2xl border border-sand-line bg-white p-4">
                  <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                  <div>
                    <p className="font-bold text-ink">{item.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-neutral-600">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s7.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s7.pBefore}
              <Link href="/blog/vide-maison-succession-bruxelles" className="font-semibold text-accent underline underline-offset-4">
                {c.s7.pLink}
              </Link>
              {c.s7.pAfter}
            </p>
          </div>

          <div className="rounded-2xl border border-sand-line bg-ink/5 p-6">
            <h2 className="font-serif text-xl font-extrabold text-brand-900">{c.retenirTitle}</h2>
            <ul className="mt-4 space-y-2">
              {c.retenir.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-neutral-700">
                  <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.faqTitle}</h2>
            <div className="mt-4 space-y-4">
              {c.faq.map((item) => (
                <div key={item.q} className="rounded-2xl border border-sand-line bg-sand p-5">
                  <p className="font-bold text-ink">{item.q}</p>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.a}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-sand-line bg-ink p-8 text-white">
            <h2 className="font-serif text-2xl font-extrabold">{c.finalCta.title}</h2>
            <p className="mt-2 text-white/75">{c.finalCta.text}</p>
            <div className="mt-6 flex flex-wrap gap-4">
              <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
                {PHONE_DISPLAY}
              </CtaLink>
              <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
                {c.finalCta.quote}
              </CtaLink>
            </div>
          </div>
        </div>
      </section>

      <Contact />
    </article>
  );
}
